// ProjectTable.jsx
import React, { useState, useRef, useEffect } from 'react'
import { deleteProject as apiDeleteProject, deleteSubtask as apiDeleteSubtask } from '../lib/api'
import { logChange } from '../lib/auditLog'
import { DEPT_COLORS, darkenHex } from '../lib/deptColors'
import { STATUSES, DEPARTMENTS, AIRCRAFT_TYPES as FALLBACK_TYPES, STATUS_STYLES, isDepartureOverdue, isArrivalOverdue } from '../lib/constants'
import { useAuth } from '../hooks/useAuth'

// ── Column filter defaults ─────────────────────────────────────────────────────
const EMPTY_COL_FILTERS = {
  aircraft: new Set(), workOrder: new Set(), customer: new Set(), hangar: new Set(),
  arrivalFrom: '', arrivalTo: '', departureFrom: '', departureTo: '',
}
const DEFAULT_SORTS = [{ col: 'departure', dir: 'asc' }]

// ── Multi-column sort (TBD/no-departure always first) ─────────────────────────
function multiSort(projects, sorts) {
  const effectiveSorts = sorts.length ? sorts : DEFAULT_SORTS
  const tbdOrNoDep = projects.filter(p => p.status === 'TBD' || !p.departure_date)
  const rest = projects.filter(p => p.status !== 'TBD' && p.departure_date)

  const tbdCmp = (a, b) => {
    if (!a.arrival_date && !b.arrival_date) return 0
    if (!a.arrival_date) return 1
    if (!b.arrival_date) return -1
    return a.arrival_date < b.arrival_date ? -1 : a.arrival_date > b.arrival_date ? 1 : 0
  }

  const restCmp = (a, b) => {
    for (const { col, dir } of effectiveSorts) {
      const aVal = col === 'arrival' ? a.arrival_date : col === 'departure' ? a.departure_date : null
      const bVal = col === 'arrival' ? b.arrival_date : col === 'departure' ? b.departure_date : null
      if (!aVal && !bVal) continue
      if (!aVal) return 1
      if (!bVal) return -1
      const diff = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      if (diff !== 0) return dir === 'asc' ? diff : -diff
    }
    return 0
  }

  return [...[...tbdOrNoDep].sort(tbdCmp), ...[...rest].sort(restCmp)]
}

// ── ColumnHeader — sortable / filterable header cell ──────────────────────────
function ColumnHeader({ label, col, sorts, onSort, filterValue, onFilter, filterType, options, align = 'left' }) {
  const [open, setOpen] = useState(false)
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 })
  const [localSearch, setLocalSearch] = useState('')
  const [snapshotOptions, setSnapshotOptions] = useState([])
  const ref = useRef(null)
  const buttonRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleOpen = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPopoverPos(align === 'right'
        ? { top: rect.bottom + 4, right: window.innerWidth - rect.right }
        : { top: rect.bottom + 4, left: rect.left }
      )
      setSnapshotOptions(options ?? [])
    }
    setOpen(o => !o)
  }

  const clearFilter = () => {
    if (filterType === 'text') onFilter(new Set())
    else if (filterType === 'dateRange') onFilter({ from: '', to: '' })
    setLocalSearch('')
    setOpen(false)
  }

  const sortEntry = col ? sorts.find(s => s.col === col) : null
  const sortPriority = col ? sorts.findIndex(s => s.col === col) : -1
  const hasFilter = filterType === 'text' ? filterValue.size > 0 : !!(filterValue?.from || filterValue?.to)
  const isActive = !!sortEntry || hasFilter

  const isSortable = !!col
  const isFilterable = !!filterType

  // Multi-select helpers — use snapshotOptions so the list stays stable while open
  const shownOptions = snapshotOptions.filter(o => !localSearch || o.toLowerCase().includes(localSearch.toLowerCase()))
  const allShownChecked = shownOptions.length > 0 && shownOptions.every(o => filterValue instanceof Set && filterValue.has(o))

  const toggleOption = (opt) => {
    const next = new Set(filterValue)
    next.has(opt) ? next.delete(opt) : next.add(opt)
    onFilter(next)
  }

  const toggleAll = () => {
    const next = new Set(filterValue)
    if (allShownChecked) shownOptions.forEach(o => next.delete(o))
    else shownOptions.forEach(o => next.add(o))
    onFilter(next)
  }

  if (!isSortable && !isFilterable) {
    return (
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
        {label}
      </span>
    )
  }

  return (
    <div ref={ref} className="inline-block">
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className={`flex items-center gap-0.5 text-xs font-semibold uppercase tracking-wide transition-colors ${isActive ? 'text-brand-red' : 'text-gray-500 hover:text-gray-700'}`}
      >
        {label}
        {sortEntry && (
          <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
              d={sortEntry.dir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
          </svg>
        )}
        {sorts.length > 1 && sortPriority >= 0 && (
          <span className="text-[9px] leading-none font-bold -mt-2">{sortPriority + 1}</span>
        )}
        {hasFilter && (
          <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L13 10.414V17a1 1 0 01-.553.894l-4 2A1 1 0 017 19v-8.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
          </svg>
        )}
      </button>

      {open && (
        <div
          style={{ position: 'fixed', top: popoverPos.top, ...(popoverPos.right !== undefined ? { right: popoverPos.right } : { left: popoverPos.left }), zIndex: 9999 }}
          className="bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-max max-w-[300px]"
        >
          {/* Header row with label + X to clear filter */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-600">{label}</span>
            {hasFilter && (
              <button onClick={clearFilter} className="p-0.5 text-gray-400 hover:text-red-500 transition-colors rounded" title="Clear filter">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Sort controls */}
          {isSortable && (
            <div className={isFilterable ? 'mb-3 pb-3 border-b border-gray-100' : ''}>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Sort</p>
              <div className="flex gap-1.5">
                <button
                  onClick={() => onSort(col, 'asc')}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    sortEntry?.dir === 'asc' ? 'bg-brand-red text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
                  </svg>
                  Asc
                </button>
                <button
                  onClick={() => onSort(col, 'desc')}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    sortEntry?.dir === 'desc' ? 'bg-brand-red text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                  Desc
                </button>
                {sortEntry && (
                  <button
                    onClick={() => onSort(col, null)}
                    className="px-2.5 py-1 rounded text-xs font-medium bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              {sorts.length > 1 && sortPriority >= 0 && (
                <p className="text-[10px] text-gray-400 mt-1.5">Sort priority: {sortPriority + 1}</p>
              )}
            </div>
          )}

          {/* Text filter — search input + checkbox list */}
          {filterType === 'text' && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Filter</p>
              <input
                type="text"
                value={localSearch}
                onChange={e => setLocalSearch(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red/30 mb-1"
                autoFocus
              />
              {options?.length > 0 && (
                <div className="border border-gray-100 rounded-md overflow-hidden">
                  {/* Select all row */}
                  <button
                    onClick={toggleAll}
                    className="flex items-center gap-2 w-full px-2.5 py-1.5 text-xs text-gray-500 hover:bg-gray-50 border-b border-gray-100 transition-colors"
                  >
                    <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${allShownChecked ? 'bg-brand-red border-brand-red' : 'border-gray-300'}`}>
                      {allShownChecked && (
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className="italic">Select All</span>
                  </button>
                  {/* Option rows */}
                  <div className="max-h-44 overflow-y-auto">
                    {shownOptions.length === 0
                      ? <p className="px-2.5 py-2 text-xs text-gray-400 text-center">No matches</p>
                      : shownOptions.map(opt => {
                          const checked = filterValue.has(opt)
                          return (
                            <button
                              key={opt}
                              onClick={() => toggleOption(opt)}
                              className="flex items-center gap-2 w-full px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                            >
                              <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${checked ? 'bg-brand-red border-brand-red' : 'border-gray-300'}`}>
                                {checked && (
                                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              {opt}
                            </button>
                          )
                        })
                    }
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Date range filter */}
          {filterType === 'dateRange' && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Filter Range</p>
              <div className="flex flex-col gap-1.5">
                <div>
                  <label className="text-[10px] text-gray-400 block mb-0.5">From</label>
                  <input
                    type="date"
                    value={filterValue?.from || ''}
                    onChange={e => onFilter({ ...filterValue, from: e.target.value })}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red/30"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 block mb-0.5">To</label>
                  <input
                    type="date"
                    value={filterValue?.to || ''}
                    onChange={e => onFilter({ ...filterValue, to: e.target.value })}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red/30"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Supporting components ──────────────────────────────────────────────────────

function MultiSelectDropdown({ label: labelProp, options, selected, onToggle }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])
  const label = selected.size === 0 ? (labelProp ?? 'All')
    : selected.size <= 2 ? [...selected].join(', ')
    : `${selected.size} selected`
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-700 transition-colors min-w-[60px]"
      >
        <span className="flex-1 text-left">{label}</span>
        <svg className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[130px] py-1">
          {options.map(opt => (
            <button key={opt} onClick={() => onToggle(opt)}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 text-left"
            >
              <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${selected.has(opt) ? 'bg-brand-red border-brand-red' : 'border-gray-300'}`}>
                {selected.has(opt) && (
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function DeptBadge({ dept }) {
  const { profile } = useAuth()
  const orgDeptMap = Object.fromEntries((profile?.orgs?.settings?.departments ?? []).map(d => [d.key, d]))
  const hex = orgDeptMap[dept]?.hex
  if (hex) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full font-accent"
        style={{ backgroundColor: hex + '33', color: darkenHex(hex) }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: darkenHex(hex) }} />
        {dept}
      </span>
    )
  }
  const c = DEPT_COLORS.EXT
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full font-accent ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {dept}
    </span>
  )
}

function StatusBadge({ status }) {
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full font-accent ${STATUS_STYLES[status] || STATUS_STYLES['Scheduled']}`}>
      {status}
    </span>
  )
}

const RISK_STYLES = {
  ahead: 'bg-green-100 text-green-700',
  tracking: 'bg-blue-50 text-blue-700',
  at_risk: 'bg-amber-100 text-amber-700',
  delayed: 'bg-red-100 text-red-700',
}
const RISK_LABELS = {
  ahead: 'Ahead',
  tracking: 'Tracking',
  at_risk: 'At Risk',
  delayed: 'Delayed',
}

function RiskBadge({ status }) {
  if (!status) return <span className="text-gray-400 text-xs">—</span>
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full font-accent ${RISK_STYLES[status] || 'bg-gray-100 text-gray-500'}`}>
      {RISK_LABELS[status] || status}
    </span>
  )
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function daysBetween(a, b) {
  if (!a || !b) return '—'
  return `${Math.round((new Date(b) - new Date(a)) / 86400000) + 1}d`
}

// ── ProjectTable ───────────────────────────────────────────────────────────────

export default function ProjectTable({
  projects, subtasks, onEditProject, onAddSubtask, onEditSubtask, refetch,
  search, setSearch, statusFilter, toggleStatus,
  deptFilter, toggleDept, typeFilter, toggleType,
  onClearAll,
  isEditor, canEditSubtasks, onNavigateToWorkflow, onWorkOrderClick, userProfile,
}) {

  const { profile } = useAuth()
  const AIRCRAFT_TYPES = profile?.orgs?.settings?.aircraftTypes ?? FALLBACK_TYPES
  const orgDepts = profile?.orgs?.settings?.departments
    ?? DEPARTMENTS.map(k => ({ key: k, label: k, hex: DEPT_COLORS[k]?.hex || '#9ca3af' }))

  const tableScrollRef = useRef(null)
  const scrollPositionRef = useRef(0)

  useEffect(() => {
    const el = tableScrollRef.current
    if (!el) return
    const handleScroll = () => { scrollPositionRef.current = el.scrollTop }
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const el = tableScrollRef.current
    if (!el) return
    if (scrollPositionRef.current > 0) el.scrollTop = scrollPositionRef.current
  })

  const [expandedRows, setExpandedRows] = useState(() => {
    try {
      const saved = sessionStorage.getItem('expandedRows')
      return saved ? new Set(JSON.parse(saved)) : new Set()
    } catch { return new Set() }
  })

  const toggleExpanded = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) } else { next.add(id) }
      sessionStorage.setItem('expandedRows', JSON.stringify([...next]))
      return next
    })
  }

  // ── Multi-sort state ─────────────────────────────────────────────────────────
  const [sorts, setSorts] = useState(() => {
    const saved = sessionStorage.getItem('tableSortBy')
    return saved ? [{ col: saved, dir: 'asc' }] : DEFAULT_SORTS
  })

  const handleSort = (col, dir) => {
    setSorts(prev => {
      const next = prev.filter(s => s.col !== col)
      if (dir) next.push({ col, dir })
      const result = next.length ? next : DEFAULT_SORTS
      sessionStorage.setItem('tableSortBy', result[0].col)
      return result
    })
  }

  // ── Column filter state ──────────────────────────────────────────────────────
  const [colFilters, setColFilters] = useState(EMPTY_COL_FILTERS)
  const setTextFilter = (key) => (newSet) => setColFilters(prev => ({ ...prev, [key]: newSet }))

  // ── Clear all ────────────────────────────────────────────────────────────────
  const hasAnyFilter =
    colFilters.aircraft.size > 0 || colFilters.workOrder.size > 0 ||
    colFilters.customer.size > 0 || colFilters.hangar.size > 0 ||
    !!colFilters.arrivalFrom || !!colFilters.arrivalTo ||
    !!colFilters.departureFrom || !!colFilters.departureTo ||
    JSON.stringify(sorts) !== JSON.stringify(DEFAULT_SORTS) ||
    !!search || statusFilter.size > 0 || deptFilter.size > 0 || typeFilter.size > 0

  const clearAll = () => {
    setColFilters(EMPTY_COL_FILTERS)
    setSorts(DEFAULT_SORTS)
    sessionStorage.setItem('tableSortBy', 'departure')
    onClearAll?.()
  }

  // ── Delete handlers ──────────────────────────────────────────────────────────
  const deleteProject = async (id) => {
    if (!confirm('Delete this project and all its subtasks?')) return
    const project = projects.find(p => p.id === id)
    await logChange({
      projectId: id, entityType: 'project', entityId: id,
      entityLabel: project?.aircraft?.registration || project?.work_order,
      action: 'deleted', userProfile,
    })
    await apiDeleteProject(id)
    refetch()
  }

  const deleteSubtask = async (id) => {
    if (!confirm('Delete this subtask?')) return
    const sub = subtasks.find(s => s.id === id)
    await logChange({
      projectId: sub?.project_id, entityType: 'subtask', entityId: id,
      entityLabel: sub?.task_name, action: 'deleted', userProfile,
    })
    await apiDeleteSubtask(id)
    refetch()
  }

  // ── Filter ───────────────────────────────────────────────────────────────────
  const filtered = projects.filter(p => {
    // Global search
    if (search) {
      const term     = search.toLowerCase()
      const reg      = (p.aircraft?.registration || '').toLowerCase()
      const alt      = (p.secondary_registration || '').toLowerCase()
      const customer = (p.customer_name || '').toLowerCase()
      const ws       = (p.workscope || '').toLowerCase()
      if (!reg.includes(term) && !alt.includes(term) && !customer.includes(term) && !ws.includes(term)) return false
    }
    // Status filter
    if (statusFilter.size === 0 && p.status === 'Complete') return false
    if (statusFilter.size > 0 && !statusFilter.has(p.status)) return false
    // Dept filter
    if (deptFilter.size > 0) {
      const ps = subtasks.filter(s => s.project_id === p.id)
      const match = [...deptFilter].some(d =>
        (p.departments || []).includes(d) || ps.some(s => s.department === d)
      )
      if (!match) return false
    }
    // Type filter
    if (typeFilter.size > 0 && !typeFilter.has(p.aircraft?.aircraft_type || 'Jet')) return false
    // Column filters
    if (colFilters.aircraft.size > 0 && !colFilters.aircraft.has(p.aircraft?.registration)) return false
    if (colFilters.workOrder.size > 0 && !colFilters.workOrder.has(p.work_order)) return false
    if (colFilters.customer.size > 0 && !colFilters.customer.has(p.customer_name)) return false
    if (colFilters.hangar.size > 0 && !colFilters.hangar.has(p.hangar_location)) return false
    if (colFilters.arrivalFrom && p.arrival_date && p.arrival_date < colFilters.arrivalFrom) return false
    if (colFilters.arrivalTo && p.arrival_date && p.arrival_date > colFilters.arrivalTo) return false
    if (colFilters.departureFrom && p.departure_date && p.departure_date < colFilters.departureFrom) return false
    if (colFilters.departureTo && p.departure_date && p.departure_date > colFilters.departureTo) return false
    return true
  })

  const sorted = multiSort(filtered, sorts)

  // ── Unique option lists for column filters (derived from current filtered set) ─
  const optAircraft  = [...new Set(filtered.map(p => p.aircraft?.registration).filter(Boolean))].sort()
  const optWorkOrder = [...new Set(filtered.map(p => p.work_order).filter(Boolean))].sort()
  const optCustomer  = [...new Set(filtered.map(p => p.customer_name).filter(Boolean))].sort()
  const optHangar    = [...new Set(filtered.map(p => p.hangar_location).filter(Boolean))].sort()

  // ── Shared ColumnHeader props ────────────────────────────────────────────────
  const chProps = { sorts, onSort: handleSort }

  return (
    <div>
      {/* Inline controls */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-3 py-3 mb-4 flex items-center gap-2 flex-wrap md:sticky md:top-0 md:z-20">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search registration, customer..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 w-full sm:w-48"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Type</span>
          <MultiSelectDropdown options={AIRCRAFT_TYPES} selected={typeFilter} onToggle={toggleType} />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Status</span>
          <MultiSelectDropdown options={STATUSES} selected={statusFilter} onToggle={toggleStatus} />
        </div>

        <div className="ml-auto flex items-center gap-2">
          {hasAnyFilter && (
            <button
              onClick={clearAll}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear All
            </button>
          )}
          <div className="flex items-center gap-1">
            {orgDepts.map(({ key: d, hex: dHex }) => {
              const isActive = deptFilter.size === 0 || deptFilter.has(d)
              return (
                <button
                  key={d}
                  onClick={() => toggleDept(d)}
                  className="px-2 py-0.5 rounded text-xs font-medium text-white transition-opacity"
                  style={{ backgroundColor: dHex, opacity: isActive ? 1 : 0.3 }}
                >
                  {d}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Table */}
      <div id="project-table-scroll" ref={tableScrollRef} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        <table className="w-full text-sm" style={{ minWidth: 600 }}>
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left px-2 md:px-4 py-3 w-8"></th>
              <th className="text-left px-2 md:px-4 py-3">
                <ColumnHeader label="Aircraft" filterType="text" filterValue={colFilters.aircraft} onFilter={setTextFilter('aircraft')} options={optAircraft} {...chProps} />
              </th>
              <th className="text-left px-2 md:px-4 py-3">
                <ColumnHeader label="Workscope" {...chProps} />
              </th>
              <th className="hidden lg:table-cell text-left px-2 md:px-4 py-3">
                <ColumnHeader label="Work Order" filterType="text" filterValue={colFilters.workOrder} onFilter={setTextFilter('workOrder')} options={optWorkOrder} {...chProps} />
              </th>
              <th className="hidden lg:table-cell text-left px-2 md:px-4 py-3">
                <ColumnHeader label="Customer" filterType="text" filterValue={colFilters.customer} onFilter={setTextFilter('customer')} options={optCustomer} {...chProps} />
              </th>
              <th className="text-left px-2 md:px-4 py-3">
                <ColumnHeader label="Dept" {...chProps} />
              </th>
              <th className="text-left px-2 md:px-4 py-3">
                <ColumnHeader label="Arrival" col="arrival" filterType="dateRange"
                  filterValue={{ from: colFilters.arrivalFrom, to: colFilters.arrivalTo }}
                  onFilter={v => setColFilters(prev => ({ ...prev, arrivalFrom: v.from, arrivalTo: v.to }))}
                  {...chProps} />
              </th>
              <th className="text-left px-2 md:px-4 py-3">
                <ColumnHeader label="Departure" col="departure" filterType="dateRange"
                  filterValue={{ from: colFilters.departureFrom, to: colFilters.departureTo }}
                  onFilter={v => setColFilters(prev => ({ ...prev, departureFrom: v.from, departureTo: v.to }))}
                  {...chProps} />
              </th>
              <th className="hidden md:table-cell text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 md:px-4 py-3">Duration</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 md:px-4 py-3">Status</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 md:px-4 py-3">Risk</th>
              <th className="hidden md:table-cell text-left px-2 md:px-4 py-3">
                <ColumnHeader label="Hangar" filterType="text" filterValue={colFilters.hangar} onFilter={setTextFilter('hangar')} options={optHangar} align="right" {...chProps} />
              </th>
              <th className="px-2 md:px-4 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td colSpan={13} className="text-center text-gray-400 py-16 text-sm">
                  No projects match your filters.
                </td>
              </tr>
            )}
            {sorted.map((project, i) => {
              const isTBD = project.status === 'TBD'
              const arrivalOverdue = isArrivalOverdue(project)
              const departureOverdue = isDepartureOverdue(project)
              const projectSubtasks = subtasks.filter(s => s.project_id === project.id)
              const visibleSubtasks = (deptFilter.size > 0
                ? projectSubtasks.filter(s => deptFilter.has(s.department))
                : projectSubtasks
              ).slice().sort((a, b) => {
                if (!a.start_date && !b.start_date) return 0
                if (!a.start_date) return 1
                if (!b.start_date) return -1
                return a.start_date < b.start_date ? -1 : a.start_date > b.start_date ? 1 : 0
              })
              const isExpanded = expandedRows.has(project.id)
              const reg = project.aircraft?.registration || '—'
              const model = [project.aircraft?.manufacturer, project.aircraft?.model].filter(Boolean).join(' ')

              return (
                <React.Fragment key={project.id}>
                  <tr className={`border-b border-gray-100 hover:bg-gray-50 transition-colors group ${
                    isTBD ? 'bg-yellow-50/40' : i % 2 === 0 ? '' : 'bg-gray-50/30'
                  }`}>
                    <td className="px-2 md:px-4 py-3">
                      {visibleSubtasks.length > 0 && (
                        <button onClick={() => toggleExpanded(project.id)}
                          className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors">
                          <svg className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                    </td>

                    <td className="px-2 md:px-4 py-3">
                      <div
                        className={`inline-flex flex-col px-2 py-0.5 rounded-lg ${isTBD ? 'bg-yellow-300' : ''} ${onNavigateToWorkflow ? 'cursor-pointer hover:opacity-70 transition-opacity' : ''}`}
                        onClick={onNavigateToWorkflow ? () => onNavigateToWorkflow(project.id) : undefined}
                        title={onNavigateToWorkflow ? 'Open project workflow' : undefined}
                      >
                        <span className="font-bold text-gray-900 tracking-wide text-sm">{reg}</span>
                        {model && <span className="text-xs text-gray-400">{model}</span>}
                      </div>
                    </td>

                    <td className="px-2 md:px-4 py-3 text-gray-600 text-sm max-w-xs truncate">{project.workscope || '—'}</td>
                    <td className="hidden lg:table-cell px-2 md:px-4 py-3 text-sm">
                      {project.work_order
                        ? onWorkOrderClick
                          ? <button onClick={() => onWorkOrderClick(project.work_order)} className="text-brand-red hover:text-brand-red-dark hover:underline font-medium">
                              {project.work_order}
                            </button>
                          : <span className="text-gray-500">{project.work_order}</span>
                        : <span className="text-gray-400">—</span>
                      }
                    </td>
                    <td className="hidden lg:table-cell px-2 md:px-4 py-3 text-gray-600 text-sm">{project.customer_name || '—'}</td>
                    <td className="px-2 md:px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(project.departments || ['MX']).map(d => <DeptBadge key={d} dept={d} />)}
                      </div>
                    </td>

                    <td className="px-2 md:px-4 py-3 whitespace-nowrap">
                      <span className={`text-sm font-medium font-accent ${arrivalOverdue ? 'text-red-600' : 'text-gray-600'}`}>
                        {formatDate(project.arrival_date)}
                      </span>
                      {arrivalOverdue && <div className="text-xs text-red-400 font-medium font-accent">Overdue</div>}
                    </td>

                    <td className="px-2 md:px-4 py-3 whitespace-nowrap">
                      <span className={`text-sm font-medium font-accent ${departureOverdue ? 'text-red-600' : 'text-gray-600'}`}>
                        {formatDate(project.departure_date)}
                      </span>
                      {departureOverdue && <div className="text-xs text-red-400 font-medium font-accent">Overdue</div>}
                    </td>

                    <td className="hidden md:table-cell px-2 md:px-4 py-3 text-gray-500 text-xs">{daysBetween(project.arrival_date, project.departure_date)}</td>
                    <td className="px-2 md:px-4 py-3"><StatusBadge status={project.status} /></td>
                    <td className="px-2 md:px-4 py-3"><RiskBadge status={project.schedule_status} /></td>
                    <td className="hidden md:table-cell px-2 md:px-4 py-3 text-gray-600 text-sm">{project.hangar_location || '—'}</td>
                    <td className="px-2 md:px-4 py-3">
                      {isEditor && <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        <button onClick={() => onEditProject(project)}
                          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button onClick={() => onAddSubtask(project.id)}
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                        <button onClick={() => deleteProject(project.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>}
                    </td>
                  </tr>

                  {isExpanded && visibleSubtasks.map(subtask => (
                    <tr key={subtask.id} className="border-b border-gray-100 bg-gray-50/30 hover:bg-gray-50/60 transition-colors group">
                      <td className="px-2 md:px-4 py-2" />
                      <td className="px-2 md:px-4 py-2 pl-6 md:pl-8" colSpan={1}>
                        <div className="flex items-center gap-2 text-gray-600">
                          <svg className="w-3 h-3 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="text-sm font-medium text-gray-700">{subtask.task_name}</span>
                        </div>
                      </td>
                      <td className="px-2 md:px-4 py-2 text-gray-400 text-xs italic" colSpan={2}>subtask</td>
                      <td className="hidden lg:table-cell px-2 md:px-4 py-2" />
                      <td className="px-2 md:px-4 py-2"><DeptBadge dept={subtask.department} /></td>
                      <td className="px-2 md:px-4 py-2 text-gray-500 text-xs whitespace-nowrap">{formatDate(subtask.start_date)}</td>
                      <td className="px-2 md:px-4 py-2 text-gray-500 text-xs whitespace-nowrap">{formatDate(subtask.end_date)}</td>
                      <td className="hidden md:table-cell px-2 md:px-4 py-2 text-gray-400 text-xs">{daysBetween(subtask.start_date, subtask.end_date)}</td>
                      <td className="px-2 md:px-4 py-2"><StatusBadge status={subtask.status} /></td>
                      <td className="px-2 md:px-4 py-2" />
                      <td className="hidden md:table-cell px-2 md:px-4 py-2" />
                      <td className="px-2 md:px-4 py-2">
                        {canEditSubtasks && <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                          <button onClick={() => onEditSubtask(subtask)}
                            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button onClick={() => deleteSubtask(subtask.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
